import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ContactShort } from '@/services/ws-funnel/src/main/resources/rest-api';
import { getSalesContacts } from '@/services/ws-funnel/src/main/resources/rest-api';
import { StyledAsyncSelect } from '@/js/libs/ui';
import { useLazyRequestLegacy } from '@/js/libs/use-request-legacy';

import { DEFAULT_PAGE, DROP_DOWN_ENTITY_LIMIT } from 'src/constants.tsx';
import { isOption } from 'src/typeGuards.ts';
import type { Option } from 'src/types.ts';

type SalesPersonSelectProps = {
  onChange: (newAssignee: Option<ContactShort> | null) => void;
  isDisabled?: boolean;
  value?: Option<ContactShort> | null;
  isClearable?: boolean;
};

const assigneeToOption = (user: ContactShort): Option<ContactShort> => ({
  value: user,
  label: user.name,
});

export const AssigneeSelect: React.FC<SalesPersonSelectProps> = ({ onChange, isDisabled, value, isClearable = true }) => {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard']);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleChange = React.useCallback(
    (opt: unknown) => {
      if (isOption<ContactShort>(opt)) {
        onChange(opt);
      } else {
        onChange(null);
      }
    },
    [onChange],
  );

  const [loadSalesContacts, { pending: isLoadingOptions }] = useLazyRequestLegacy({
    apiMethod: getSalesContacts,
  });

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleLoadOptions = React.useCallback(
    async (prefix: string): Promise<Option<ContactShort>[]> => {
      const { items } = await loadSalesContacts({
        filter: prefix,
        includeInactive: false,
        page: DEFAULT_PAGE,
        limit: DROP_DOWN_ENTITY_LIMIT,
      });
      return items.map(assigneeToOption);
    },
    [loadSalesContacts],
  );

  return (
    <StyledAsyncSelect<Option<ContactShort>, false>
      placeholder={t('serviceDashboard:SELECT_ASSIGNEE')}
      onChange={handleChange}
      value={value}
      isClearable={isClearable}
      isLoading={isLoadingOptions}
      isDisabled={isDisabled}
      loadOptions={handleLoadOptions}
      defaultOptions
    />
  );
};
